<template>
  <div class="block">
    <div :class="['box is-paddingless', $style.userListContainer]">
      <table class="table is-fullwidth">
        <thead>
          <tr>
            <th>
              Email
              <b-icon
                v-if="
                  filteredParams.orderBy.field === 'email' &&
                  filteredParams.orderBy.sort === 'DESC'
                "
                icon="arrow-up"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('email', 'ASC')"
              ></b-icon>
              <b-icon
                v-else
                icon="arrow-down"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('email', 'DESC')"
              ></b-icon>
            </th>
            <th>
              First Name
              <b-icon
                v-if="
                  filteredParams.orderBy.field === 'first_name' &&
                  filteredParams.orderBy.sort === 'DESC'
                "
                icon="arrow-up"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('first_name', 'ASC')"
              ></b-icon>
              <b-icon
                v-else
                icon="arrow-down"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('first_name', 'DESC')"
              ></b-icon>
            </th>
            <th>
              Last Name
              <b-icon
                v-if="
                  filteredParams.orderBy.field === 'last_name' &&
                  filteredParams.orderBy.sort === 'DESC'
                "
                icon="arrow-up"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('last_name', 'ASC')"
              ></b-icon>
              <b-icon
                v-else
                icon="arrow-down"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('last_name', 'DESC')"
              ></b-icon>
            </th>
            <th width="15%">
              Username
              <b-icon
                v-if="
                  filteredParams.orderBy.field === 'username' &&
                  filteredParams.orderBy.sort === 'DESC'
                "
                icon="arrow-up"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('username', 'ASC')"
              ></b-icon>
              <b-icon
                v-else
                icon="arrow-down"
                size="is-small"
                :class="$style.orderBy"
                @click.native="updateSort('username', 'DESC')"
              ></b-icon>
            </th>
            <th>Status</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="item in items" :key="item.id">
            <td>{{ item.email || '-' }}</td>
            <td>{{ item.firstName || '-' }}</td>
            <td>{{ item.lastName || '-' }}</td>
            <td>{{ item.username || '-' }}</td>
            <td>{{ getSubscriptionStatus(item) || '-' }}</td>
          </tr>
        </tbody>

        <tfoot v-if="items && items.length === 0">
          <tr>
            <td colspan="100%" class="is-paddingless">
              <p class="p-2 has-text-centered">No Data Found</p>
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
    <BasePagination
      v-if="items && items.length > 0"
      :page.sync="page"
      :perPage.sync="perPage"
      :totalNumberOfPage="totalNumberOfPage"
      :totalCount="totalCount"
      :isFirstPage="isFirstPage"
      :isLastPage="isLastPage"
      :startItemIndex="startItemIndex"
      :endItemIndex="endItemIndex"
    />
  </div>
</template>

<script lang="ts">
import {
  Component,
  Vue,
  Prop,
  Watch,
  Model,
  Emit,
} from 'vue-property-decorator';
import { State, Getter, Action, Mutation, namespace } from 'vuex-class';
import { AxiosError } from 'axios';
import BasePagination from '@/components/base/BasePagination.vue';
import BaseModal from '@/jbi-shared/vue-components/BaseModal.vue';
import { RootState } from '@/store/store';
import { isDifferent, isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  User,
  Invitation,
  FilteredUserPayload,
} from '@/store/modules/users/types/users.types';
import { mixins } from 'vue-class-component';
import { PaginationMixin } from '@/components/base/pagination.mixin';
import {
  ToastProgrammatic as Toast,
  DialogProgrammatic as Dialog,
} from 'buefy';
import { ApiState } from '@/store/types/general.types';
import { get as _get } from 'lodash';
import BaseDialog from '../../../components/base/BaseDialog.vue';
import dayjs from 'dayjs';

@Component({
  components: {
    BaseModal,
    BasePagination,
  },
})
export default class UserList extends mixins(PaginationMixin) {
  @Prop() public items!: User[];
  @Prop(Number) public currentPage!: number;
  @Prop(Number) public totalNumberOfPage!: number;
  @Prop(Number) public totalCount!: number;
  @Prop(Boolean) public isFirstPage!: boolean;
  @Prop(Boolean) public isLastPage!: boolean;
  @Prop(Number) public startItemIndex!: number;
  @Prop(Number) public endItemIndex!: number;
  @Prop() public filteredParams!: FilteredUserPayload;

  @Action('users/updateStatus')
  public updateStatus!: (userId: number) => void;

  public toggleStatus({ username, userId, email, status }: User) {
    const isInactive = status === 'Inactive';
    const titleStatus = isInactive
      ? `Activate ${username}?`
      : `Deactivate ${username}?`;
    const msg = isInactive
      ? `Are you sure you want to activate user <b>${username}</b>?`
      : `Are you sure you want to deactivate user <b>${username}</b>?`;

    this.$buefy.modal.open({
      parent: this,
      component: BaseDialog,
      hasModalCard: true,
      trapFocus: true,
      props: {
        title: titleStatus,
        content: msg,
        confirmButtonText: 'Cancel',
        cancelButtonText: isInactive ? 'Activate' : 'Deactivate',
      },
      events: {
        cancel: () => {
          this.updateStatus(userId);
        },
      },
    });
  }

  public updateSort(field: string, sort: string) {
    if (this.filteredParams.orderBy) {
      this.filteredParams.orderBy.field = field;
      this.filteredParams.orderBy.sort = sort;
    }
  }

  public getSubscriptionStatus(data: any) {
    let subscriptionStatus: string = '';
    const remainingDays = dayjs(data.expire_at).diff(new Date(), 'day');
    if (remainingDays <= 7) {
      subscriptionStatus =
        remainingDays <= 0 ? `Expired` : `Expiring (${remainingDays} days)`;
    } else {
      subscriptionStatus = 'Active';
    }
    return subscriptionStatus;
  }
}
</script>

<style lang="scss" module>
.userListContainer {
  border-radius: 6px;
  overflow: hidden;
  & > table {
    thead {
      background-color: #ffffff;
      tr {
        font-size: 14px;
        th {
          color: #9f9f9f;
          font-weight: initial !important;
          padding: 20px 20px 23px 12px;
        }
        th:first-child {
          padding-left: 20px;
        }
      }
    }
    tbody {
      background-color: #ffffff;
      tr {
        td {
          color: #444f51;
          border: 0.5px solid #dbdbdb;
          border-width: 0 0 0.5px;
          padding-top: 20px;
          padding-bottom: 18px;
          padding-right: 0px;
        }
        td:first-child {
          padding-left: 20px;
        }
      }
    }
  }
}
.orderBy {
  cursor: pointer;
}
</style>
